import { FolderOpen, Sparkles } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { setActivePage } from "../app/activePage.ts";
import { useStore } from "../app/store.ts";
import { Button } from "./Button.tsx";
import { ModalShell } from "./ModalShell.tsx";
import {
  COLOR_BORDER,
  COLOR_GOLD,
  COLOR_TEXT,
  COLOR_TEXT_DIM,
  COLOR_TEXT_FAINT,
  FONT_SIZE_MODAL_TITLE,
  FONT_SIZE_SMALL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------

interface TrialStartModalProps {
  /** True when the scene has no objects — controls whether the modal appears. */
  isEmpty: boolean;
}

export function TrialStartModal({ isEmpty }: TrialStartModalProps) {
  const [dismissed, setDismissed] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const loadSceneFromFile = useStore((s) => s.loadSceneFromFile);

  const open = isEmpty && !dismissed;

  const handleStartFresh = useCallback(() => {
    setDismissed(true);
  }, []);

  const handleOpenFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        await loadSceneFromFile(file);
        setDismissed(true);
      }
      e.target.value = "";
    },
    [loadSceneFromFile],
  );

  const handleSubscribe = useCallback(() => {
    setActivePage("accountSettings");
  }, []);

  return (
    <>
      <ModalShell
        open={open}
        onClose={handleStartFresh}
        width={380}
        style={{ gap: 0, padding: 0, overflow: "hidden" }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px 16px" }}>
          <div
            style={{
              fontSize: FONT_SIZE_MODAL_TITLE,
              fontWeight: 600,
              color: COLOR_TEXT,
              marginBottom: 4,
            }}
          >
            PoseRef Trial
          </div>
          <div
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_DIM,
              lineHeight: 1.5,
            }}
          >
            The full editor, free to try. Pose, sculpt, and export without an
            account.
          </div>
        </div>

        {/* Actions */}
        <div
          style={{
            padding: "0 24px 20px",
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          <Button
            onClick={handleOpenFile}
            variant="secondary"
            size="md"
            fullWidth
            leftIcon={<FolderOpen size={15} />}
          >
            Open Scene File (.pr)…
          </Button>
          <Button
            onClick={handleStartFresh}
            variant="primary"
            size="md"
            fullWidth
          >
            Start Fresh
          </Button>
        </div>

        {/* Upgrade CTA */}
        <div
          style={{
            padding: "14px 24px",
            borderTop: `0.5px solid ${COLOR_BORDER}`,
            background: "rgba(240,192,64,0.04)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
            <Sparkles
              size={13}
              style={{ color: COLOR_GOLD, marginTop: 1, flexShrink: 0 }}
            />
            <span
              style={{
                fontSize: FONT_SIZE_SMALL,
                color: COLOR_TEXT_FAINT,
                lineHeight: 1.5,
              }}
            >
              Cloud library · All body types · GLB export
            </span>
          </div>
          <Button
            onClick={handleSubscribe}
            variant="ghost"
            size="xs"
            style={{ color: COLOR_GOLD, flexShrink: 0 }}
          >
            Subscribe
          </Button>
        </div>
      </ModalShell>

      <input
        ref={fileInputRef}
        type="file"
        accept=".pr"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />
    </>
  );
}
